import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import { Check, LoaderCircle, TerminalSquare, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  backgroundOutputCompletion,
  backgroundOutputTail,
  backgroundTerminalRunning,
  terminalExitLabel,
} from '@/lib/conversations/message-part/terminal';
import type { TerminalPart } from '@/lib/conversations/message-part/types';
import { backgroundOutputFile } from '@/lib/desktop-panel-helpers';
import { readNativeAgentBackgroundOutput } from '@/lib/native-desktop';
import { TRANSCRIPT_CARD } from './command-card';

const BACKGROUND_CARD_REFRESH_MS = 2_000;

/**
 * Inline transcript card for a backgrounded agent terminal, mirroring the
 * sub-agent card: a compact summary with a running/exited status indicator that
 * opens the terminal side panel when clicked. The command label is only known
 * when the card is rendered next to its originating tool call, so it is
 * optional; otherwise we fall back to the streamed output file.
 */
export function BackgroundTerminalCard({
  command,
  data,
  onOpen,
}: {
  command?: string;
  data: TerminalPart['data'];
  onOpen?: () => void;
}) {
  const outputFile = backgroundOutputFile(data.output);
  // The terminal part reports the launcher shell's exit status. For a
  // background command, Exit 0 only means that the launcher detached the task
  // successfully; it does not mean the task itself stopped.
  const [backgroundOutput, setBackgroundOutput] = useState('');
  const completion = backgroundOutputCompletion(backgroundOutput);
  const completed = data.backgroundCompleted === true || completion !== null;
  const exitCode = completion?.exitCode ?? data.exitCode;
  const signal = completion?.signal ?? data.signal;
  const running = backgroundTerminalRunning(
    data.exitCode,
    data.signal,
    outputFile !== null,
    completed,
  );
  const failed = exitCode !== null && exitCode !== 0;
  const StatusIcon = running ? LoaderCircle : failed ? XCircle : Check;
  const statusLabel =
    signal !== null ? 'Stopped' : running ? 'Running' : terminalExitLabel(exitCode, null);
  const interactive = onOpen !== undefined;
  const outputTail = backgroundOutputTail(backgroundOutput);

  useEffect(() => {
    if (outputFile === null) {
      setBackgroundOutput('');
      return;
    }
    let cancelled = false;
    const refresh = () => {
      void readNativeAgentBackgroundOutput(outputFile)
        .then((output) => {
          if (!cancelled) setBackgroundOutput(output);
        })
        .catch(() => {
          // The output file may be created shortly after the terminal card.
          // Keep the last successful tail while the next refresh retries.
        });
    };
    refresh();
    if (!running)
      return () => {
        cancelled = true;
      };
    const interval = window.setInterval(refresh, BACKGROUND_CARD_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [outputFile, running]);

  // A server says where it listens: "Running · localhost:5173".
  const address = serverAddress(`${backgroundOutput}\n${data.output}`);
  const detail = [statusLabel, address].filter(Boolean).join(' · ');

  return (
    <section
      className={cn(
        TRANSCRIPT_CARD,
        'flex min-w-0 items-center gap-2.5 px-3.5 py-2.5 text-foreground/85',
      )}
      data-background-terminal
    >
      {running ? (
        <LoaderCircle aria-hidden className="size-3.5 shrink-0 animate-spin text-running" />
      ) : (
        <StatusIcon
          aria-hidden
          className={cn('size-3.5 shrink-0', failed ? 'text-destructive' : 'text-success')}
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-mono text-foreground text-sm">
          {command ?? (
            <span className="inline-flex items-center gap-1.5 font-sans">
              <TerminalSquare aria-hidden className="size-3.5" /> Background terminal
            </span>
          )}
        </p>
        <p
          className={cn(
            'type-meta mt-0.5 truncate',
            failed ? 'text-destructive' : 'text-muted-foreground',
          )}
          title={outputTail || undefined}
        >
          {detail}
        </p>
      </div>
      {interactive && (
        <Button
          className="h-8 shrink-0 rounded-control px-3.5"
          onClick={onOpen}
          size="sm"
          variant="outline"
        >
          Open
        </Button>
      )}
    </section>
  );
}

/** Where a server the command started listens, from its output ("localhost:5173"). */
function serverAddress(output: string): string | null {
  const match = /\bhttps?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:\d+)?/i.exec(output);
  if (match === null) return null;
  return `localhost${match[2] ?? ''}`;
}
